import React, { FC, useMemo } from 'react';
import { Drawer as DrawerCore, SideNavLink, SideNavAnchor, useScopedSelector } from 'ia-react-core';
import { useDispatch } from 'react-redux';
import { openProfileToggle } from '~/app/App.actions';
import { useTranslation } from 'react-i18next';
import { redirectTo, useFeatureState } from 'omni-package-react-core';
import { AppState } from '~/interfaces/AppState';
import { PersonalInformationsState, PersonalInformation } from 'we-package-react-personal-informations';
import { useLogout } from '~/hooks/useLogout';
import getAnalyticsManager from '~/utils/getAnalyticsManager';
import { AnalyticsEventInteractionDataLayer } from 'omni-package-react-analytics';
import getTokenManager from '~/utils/getTokenManager';
import { useNavigate } from 'react-router-dom';
import { StyledDrawerSideNav } from './ProfileDrawer.styles';
import ChangeLanguageButton from './ChangeLanguageButton';
import getPersonalInformation from './getPersonalInformation';

export interface ProfileDrawerProps { }

const ProfileDrawer: FC<ProfileDrawerProps> = () => {
  const analyticsManager = getAnalyticsManager();
  const isOpen: boolean = useScopedSelector((state: AppState) => state.isProfileDrawerOpen);
  const personalInformation: PersonalInformation = useFeatureState(PersonalInformationsState, (state) => state?.info);
  const personalInfos = getPersonalInformation(personalInformation);
  const navigate = useNavigate();
  const tokenManager = useMemo(() => getTokenManager(), []);

  const dispatch = useDispatch();
  const { logout } = useLogout();
  const dispatchOpenProfileToggle = () => {
    dispatch(openProfileToggle());
  };

  const navigateToProfile = async (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    try {
      // Keep SPA navigation while proactively refreshing token for profile requests.
      await tokenManager.refresh();
    } catch {
      // Keep navigation path unchanged even if refresh cannot complete.
    }

    dispatchOpenProfileToggle();
    redirectTo('/profile', navigate);
  };
  const sendCustomAnalyticsEvent = () => {
    const data: AnalyticsEventInteractionDataLayer = {
      event: 'logout',
      method: 'manual',
      interaction: {
        form_name: 'client_space',
        form_step: 'logout',
        form_type: 'logout',
        form_section: 'logout',
      },
    };
    analyticsManager.customInteractionEvent(data);
  };

  const { t } = useTranslation('App');

  return (
    <DrawerCore
      name="Profile"
      noContentPadding
      drawerTitle={personalInfos.fullName}
      drawerSubTitle={personalInfos.email}
      isOpened={isOpen}
      position="right"
      onClose={() => dispatchOpenProfileToggle()}
    >
      <DrawerCore.Content>
        <StyledDrawerSideNav>
          <SideNavLink
            data-testid="MyProfile"
            to="/profile"
            onClick={navigateToProfile}
          >
            {t('menu.profile')}
          </SideNavLink>
          <ChangeLanguageButton />
          <SideNavAnchor
            onClick={() => {
              sendCustomAnalyticsEvent();
              dispatchOpenProfileToggle();
              logout();
            }}
            data-testid="LogOffButton"
          >{t('mainHeader.logOff')}
          </SideNavAnchor>
        </StyledDrawerSideNav>
      </DrawerCore.Content>
    </DrawerCore>
  );
};

export default ProfileDrawer;
